/**
 * PlatformResources — OCR pool, storage providers, and routing assignment management.
 * Platform owner only.
 * Three tabs: Recognizer Pool | Company Groups | Storage Providers
 */
import { useEffect, useState, useCallback, useRef } from 'react';import api from '../api/client';
import {
  RefreshCw, Plus, Trash2, Eye, EyeOff, CheckCircle, XCircle, AlertTriangle,
  Server, Database, Users, Zap, Package, Activity, Settings, Edit2, BarChart2,
  PauseCircle, PlayCircle,
} from 'lucide-react';

const TABS = ['Recognizer Pool', 'Company Groups', 'Storage Providers'];
const LOAD_STRATEGY_LABELS = { round_robin: 'Round-robin', least_busy: 'Least busy', random: 'Random' };

export default function PlatformResources() {
  const [tab, setTab]       = useState(0);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const pollRef = useRef(null);

  // ── Pool data ──────────────────────────────────────────────────────────────
  const [nodes, setNodes]               = useState([]);
  const [rGroups, setRGroups]           = useState([]);
  const [rGroupMembers, setRGroupMembers] = useState([]);
  const [rAssignments, setRAssignments] = useState([]);

  // ── Analytics data ────────────────────────────────────────────────────────
  const [analytics, setAnalytics]       = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);

  // ── Company group data ────────────────────────────────────────────────────
  const [cGroups, setCGroups]           = useState([]);
  const [cGroupMembers, setCGroupMembers] = useState([]);

  // ── Storage data ──────────────────────────────────────────────────────────
  const [storageProviders, setStorageProviders] = useState([]);
  const [storageAssignments, setStorageAssignments] = useState([]);

  // ── All companies (for unassigned panel) ─────────────────────────────────
  const [allCompanies, setAllCompanies] = useState([]);

  // ── Forms ─────────────────────────────────────────────────────────────────
  const [showAddNode, setShowAddNode]           = useState(false);
  const [showAddRGroup, setShowAddRGroup]       = useState(false);
  const [showAddCGroup, setShowAddCGroup]       = useState(false);
  const [showAddStorage, setShowAddStorage]     = useState(false);
  const [newNode, setNewNode]   = useState({ nodeName: '', endpoint: '', apiKey: '', nodeType: 'azure_di', modelConfig: '', isPreview: false });
  const [newRGroup, setNewRGroup] = useState({ groupName: '', description: '' });
  const [newCGroup, setNewCGroup] = useState({ groupName: '', description: '' });
  const [newStorage, setNewStorage] = useState({ providerName: '', connectionString: '' });
  const [saving, setSaving] = useState(false);

  // ── Edit states ────────────────────────────────────────────────────────────
  const [editingGroup, setEditingGroup] = useState(null);   // { GroupId, groupName, description, loadStrategy }
  const [editingRule, setEditingRule]   = useState(null);   // assignment object being edited
  const [assigningNode, setAssigningNode] = useState({});   // { [groupId]: selectedNodeId }

  const [addingAssignmentFor, setAddingAssignmentFor] = useState(null);
  const [migrations, setMigrations]       = useState([]);
  const migPollRef = useRef(null);
  const [showMigrateModal, setShowMigrateModal] = useState(null);
  const [migrateCompany, setMigrateCompany]   = useState('');
  const [migrateToProvider, setMigrateToProvider] = useState('');
  const [migratingNow, setMigratingNow]     = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nodesRes, rGroupsRes, cGroupsRes, storageRes, statusRes, companiesRes] = await Promise.all([
        api.get('/platform/resources/nodes'),
        api.get('/platform/resources/recognizer-groups'),
        api.get('/platform/resources/company-groups'),
        api.get('/platform/resources/storage-providers'),
        api.get('/platform/resources/status'),
        api.get('/companies'),
      ]);
      setNodes(nodesRes.data.nodes || []);
      setRGroups(rGroupsRes.data.groups || []);
      setRGroupMembers(rGroupsRes.data.members || []);
      setRAssignments(rGroupsRes.data.assignments || []);
      setCGroups(cGroupsRes.data.groups || []);
      setCGroupMembers(cGroupsRes.data.members || []);
      setStorageProviders(storageRes.data.providers || []);
      setStorageAssignments(storageRes.data.assignments || []);
      setStatus(statusRes.data);
      setAllCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : (companiesRes.data.companies || []));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load resource data');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const r = await api.get('/platform/resources/analytics');
      setAnalytics(r.data);
    } catch {}
  }, []);

  useEffect(() => {
    loadAll();
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get('/platform/resources/status');
        setStatus(r.data);
      } catch {}
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [loadAll]);

  useEffect(() => {
    if (showAnalytics) loadAnalytics();
  }, [showAnalytics, loadAnalytics]);

  const loadMigrations = useCallback(async () => {
    try {
      const r = await api.get('/platform/resources/storage-migrations');
      setMigrations(r.data.migrations || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (tab !== 2) { clearInterval(migPollRef.current); return; }
    loadMigrations();
    migPollRef.current = setInterval(loadMigrations, 5000);
    return () => clearInterval(migPollRef.current);
  }, [tab, loadMigrations]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAddNode = async () => {
    if (!newNode.nodeName || !newNode.endpoint || !newNode.apiKey) return;
    setSaving(true);
    try {
      const payload = { ...newNode };
      if (payload.nodeType === 'azure_di') delete payload.modelConfig;
      if (!payload.modelConfig) delete payload.modelConfig;
      await api.post('/platform/resources/nodes', payload);
      setNewNode({ nodeName: '', endpoint: '', apiKey: '', nodeType: 'azure_di', modelConfig: '', isPreview: false });
      setShowAddNode(false);
      await loadAll();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add node');
    } finally { setSaving(false); }
  };

  const handleToggleNode = async (nodeId, current) => {
    try {
      await api.patch(`/platform/resources/nodes/${nodeId}`, { isActive: !current });
      await loadAll();
    } catch (err) { setError('Failed to update node'); }
  };

  const handleDeleteNode = async (nodeId) => {
    if (!confirm('Deactivate this node? Jobs already in flight will complete.')) return;
    try {
      await api.delete(`/platform/resources/nodes/${nodeId}`);
      await loadAll();
    } catch (err) { setError('Failed to deactivate node'); }
  };

  const handleAddRGroup = async () => {
    if (!newRGroup.groupName) return;
    setSaving(true);
    try {
      await api.post('/platform/resources/recognizer-groups', newRGroup);
      setNewRGroup({ groupName: '', description: '' });
      setShowAddRGroup(false);
      await loadAll();
    } catch (err) { setError('Failed to create recognizer group'); }
    finally { setSaving(false); }
  };

  const handleSaveEditGroup = async () => {
    if (!editingGroup) return;
    setSaving(true);
    try {
      await api.patch(`/platform/resources/recognizer-groups/${editingGroup.GroupId}`, {
        groupName: editingGroup.groupName,
        description: editingGroup.description,
        loadStrategy: editingGroup.loadStrategy,
      });
      setEditingGroup(null);
      await loadAll();
    } catch (err) { setError('Failed to update group'); }
    finally { setSaving(false); }
  };

  const handleDeleteRGroup = async (groupId, groupName) => {
    if (!confirm(`Delete group "${groupName}"? This will also remove all its assignment rules.`)) return;
    try {
      await api.delete(`/platform/resources/recognizer-groups/${groupId}`);
      await loadAll();
    } catch (err) { setError('Failed to delete group'); }
  };

  const handleToggleNodeInGroup = async (groupId, nodeId, currentDisabled) => {
    try {
      await api.patch(`/platform/resources/recognizer-groups/${groupId}/nodes/${nodeId}`, { isDisabled: !currentDisabled });
      await loadAll();
    } catch (err) { setError('Failed to update node'); }
  };

  const handleAssignNodeToGroup = async (groupId) => {
    const nodeId = assigningNode[groupId];
    if (!nodeId) return;
    try {
      await api.post(`/platform/resources/recognizer-groups/${groupId}/nodes`, { nodeId });
      setAssigningNode(prev => ({ ...prev, [groupId]: '' }));
      await loadAll();
    } catch (err) { setError('Failed to assign node'); }
  };

  const handleSaveEditRule = async () => {
    if (!editingRule) return;
    setSaving(true);
    try {
      await api.patch(`/platform/resources/recognizer-assignments/${editingRule.AssignmentId}`, {
        uploadType: editingRule.uploadType,
        scope: editingRule.scope,
        targetId: editingRule.scope !== 'all' ? editingRule.targetId : null,
        priority: parseInt(editingRule.priority, 10),
      });
      setEditingRule(null);
      await loadAll();
    } catch (err) { setError('Failed to update rule'); }
    finally { setSaving(false); }
  };

  const handleAddCGroup = async () => {
    if (!newCGroup.groupName) return;
    setSaving(true);
    try {
      await api.post('/platform/resources/company-groups', newCGroup);
      setNewCGroup({ groupName: '', description: '' });
      setShowAddCGroup(false);
      await loadAll();
    } catch (err) { setError('Failed to create company group'); }
    finally { setSaving(false); }
  };

  const handleAddStorage = async () => {
    if (!newStorage.providerName || !newStorage.connectionString) return;
    setSaving(true);
    try {
      await api.post('/platform/resources/storage-providers', newStorage);
      setNewStorage({ providerName: '', connectionString: '' });
      setShowAddStorage(false);
      await loadAll();
    } catch (err) { setError('Failed to add storage provider'); }
    finally { setSaving(false); }
  };

  const handleStartMigration = async () => {
    if (!migrateCompany || !migrateToProvider) return;
    setMigratingNow(true);
    try {
      await api.post('/platform/resources/storage-migrations', {
        companyId: migrateCompany,
        toProviderId: migrateToProvider,
      });
      setShowMigrateModal(null);
      setMigrateCompany('');
      setMigrateToProvider('');
      loadMigrations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start migration');
    } finally { setMigratingNow(false); }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const nodesForGroup = (groupId) =>
    rGroupMembers
      .filter(m => m.RecognizerGroupId === groupId)
      .map(m => ({ ...nodes.find(n => n.NodeId === m.NodeId), IsDisabled: m.IsDisabled }))
      .filter(n => n.NodeId);

  const membersForCGroup = (groupId) =>
    cGroupMembers.filter(m => m.CompanyGroupId === groupId);

  const assignedCompanyIds = new Set(cGroupMembers.map(m => m.CompanyId));

  const queueColor = (n) => n > 0 ? 'text-amber-600 font-semibold' : 'text-gray-400';

  const analyticsForNode = (nodeId) => analytics?.nodes?.find(n => n.NodeId === nodeId);
  const analyticsForGroup = (groupId) => analytics?.groupDistribution?.filter(r => r.GroupId === groupId) || [];
  const totalGroupJobs = (groupId) => analyticsForGroup(groupId).reduce((s, r) => s + (r.Jobs7d || 0), 0);

  // ── UI ─────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">OCR Pool &amp; Storage</h1>
          <p className="text-sm text-gray-500 mt-1">Manage recognizer nodes, storage providers, and routing rules</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowAnalytics(v => !v)}
            className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${showAnalytics ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white hover:bg-gray-50'}`}>
            <BarChart2 className="h-4 w-4" /> Analytics
          </button>
          <button onClick={loadAll} className="flex items-center gap-2 px-3 py-2 text-sm bg-white border rounded-lg hover:bg-gray-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />{error}
        </div>
      )}

      {/* Live queue status bar */}
      {status && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: '⚡ Priority queued',     val: status.queue?.priorityQueued     ?? '-', color: queueColor(status.queue?.priorityQueued) },
            { label: '⚡ Priority processing', val: status.queue?.priorityProcessing ?? '-', color: 'text-blue-600' },
            { label: '📦 Bulk queued',          val: status.queue?.bulkQueued         ?? '-', color: queueColor(status.queue?.bulkQueued) },
            { label: '📦 Bulk processing',      val: status.queue?.bulkProcessing     ?? '-', color: 'text-blue-600' },
            { label: '❌ Failed (7d)',           val: status.queue?.failed             ?? '-', color: (status.queue?.failed > 0) ? 'text-red-600 font-semibold' : 'text-gray-400' },
          ].map(({ label, val, color }) => (
            <div key={label} className="bg-white border rounded-lg p-3 text-center">
              <div className={`text-2xl font-bold ${color}`}>{val}</div>
              <div className="text-xs text-gray-500 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Analytics panel */}
      {showAnalytics && analytics && (
        <div className="bg-white border rounded-xl p-5 space-y-5">
          <h2 className="font-semibold text-gray-800 flex items-center gap-2"><BarChart2 className="h-4 w-4 text-indigo-500" /> Node &amp; Group Analytics (last 7 days)</h2>

          {/* Per-node table */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Per Node</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 text-left">
                    <th className="px-3 py-2 font-medium">Node</th>
                    <th className="px-3 py-2 font-medium text-right">Total ever</th>
                    <th className="px-3 py-2 font-medium text-right">7d jobs</th>
                    <th className="px-3 py-2 font-medium text-right">Avg wait</th>
                    <th className="px-3 py-2 font-medium text-right">Avg process</th>
                    <th className="px-3 py-2 font-medium text-right">Failed 7d</th>
                    <th className="px-3 py-2 font-medium">Last used</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.nodes.map(n => (
                    <tr key={n.NodeId} className="border-t hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium text-gray-800">{n.NodeName}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{n.TotalProcessed ?? 0}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{n.Jobs7d ?? 0}</td>
                      <td className="px-3 py-2 text-right">{n.AvgWaitSec != null ? `${Math.round(n.AvgWaitSec)}s` : '—'}</td>
                      <td className="px-3 py-2 text-right">{n.AvgProcessSec != null ? `${Math.round(n.AvgProcessSec)}s` : '—'}</td>
                      <td className={`px-3 py-2 text-right font-medium ${n.Failed7d > 0 ? 'text-red-600' : 'text-gray-400'}`}>{n.Failed7d ?? 0}</td>
                      <td className="px-3 py-2 text-gray-400">{n.LastUsedAt ? new Date(n.LastUsedAt).toLocaleString() : 'Never'}</td>
                    </tr>
                  ))}
                  {analytics.nodes.length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-4 text-center text-gray-400 italic">No data yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Per-group distribution */}
          {rGroups.map(g => {
            const rows = analyticsForGroup(g.GroupId);
            const total = rows.reduce((s, r) => s + (r.Jobs7d || 0), 0);
            if (rows.length === 0) return null;
            return (
              <div key={g.GroupId}>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Group: <span className="text-indigo-700">{g.GroupName}</span>
                  <span className="text-gray-400 font-normal ml-2">— {total} jobs in 7d</span>
                </h3>
                <div className="space-y-1.5">
                  {rows.map(r => {
                    const pct = total > 0 ? Math.round((r.Jobs7d / total) * 100) : 0;
                    return (
                      <div key={r.NodeId} className="flex items-center gap-3 text-xs">
                        <span className="w-32 truncate text-gray-700 font-medium">{r.NodeName}</span>
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="w-10 text-right text-gray-500">{pct}%</span>
                        <span className="w-14 text-right text-gray-400">{r.Jobs7d} jobs</span>
                        <span className="w-16 text-right text-gray-400">{r.AvgWaitSec != null ? `${Math.round(r.AvgWaitSec)}s wait` : '—'}</span>
                        <span className={`w-14 text-right ${r.Failed7d > 0 ? 'text-red-500 font-medium' : 'text-gray-300'}`}>{r.Failed7d > 0 ? `${r.Failed7d} fail` : '—'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {analytics.note && <p className="text-xs text-amber-600 bg-amber-50 rounded px-3 py-2">{analytics.note}</p>}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b flex gap-1">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${i === tab ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab 0: Recognizer Pool ─────────────────────────────────────────── */}
      {tab === 0 && (
        <div className="space-y-6">
          {/* Nodes grid */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Server className="h-4 w-4" /> Recognizer Nodes</h2>
              <button onClick={() => setShowAddNode(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                <Plus className="h-4 w-4" /> Add Node
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {nodes.map(node => (
                <NodeCard key={node.NodeId} node={node}
                  liveActiveJobs={status?.nodes?.find(n => n.NodeId === node.NodeId)?.ActiveJobs ?? node.ActiveJobs}
                  nodeAnalytics={analyticsForNode(node.NodeId)}
                  showAnalytics={showAnalytics}
                  onToggle={() => handleToggleNode(node.NodeId, node.IsActive)}
                  onDelete={() => handleDeleteNode(node.NodeId)}
                />
              ))}
            </div>
          </div>

          {/* Recognizer Groups */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Package className="h-4 w-4" /> Recognizer Groups &amp; Assignments</h2>
              <button onClick={() => setShowAddRGroup(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50">
                <Plus className="h-4 w-4" /> Add Group
              </button>
            </div>
            <div className="space-y-3">
              {rGroups.map(g => (
                <div key={g.GroupId} className="bg-white border rounded-xl p-4 space-y-3">
                  {/* Group header */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-800">{g.GroupName}</span>
                        {g.Description && <span className="text-xs text-gray-400">{g.Description}</span>}
                        <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100">
                          {LOAD_STRATEGY_LABELS[g.LoadStrategy] || g.LoadStrategy}
                        </span>
                        {showAnalytics && (
                          <span className="text-xs text-gray-400">{totalGroupJobs(g.GroupId)} jobs/7d</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setEditingGroup({ GroupId: g.GroupId, groupName: g.GroupName, description: g.Description || '', loadStrategy: g.LoadStrategy || 'round_robin' })}
                      className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg" title="Edit group">
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRGroup(g.GroupId, g.GroupName)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg" title="Delete group">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Node chips */}
                  <div className="flex flex-wrap gap-2 items-center">
                    {nodesForGroup(g.GroupId).map(n => (
                      <span key={n.NodeId}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${
                          n.IsDisabled ? 'bg-gray-100 text-gray-400 border-gray-200 line-through' :
                          n.IsActive   ? 'bg-green-100 text-green-700 border-green-200' :
                                         'bg-amber-50 text-amber-600 border-amber-200'
                        }`}>
                        {n.NodeName}
                        {/* Disable/enable in group */}
                        <button
                          onClick={() => handleToggleNodeInGroup(g.GroupId, n.NodeId, n.IsDisabled)}
                          className="ml-0.5 hover:opacity-70"
                          title={n.IsDisabled ? 'Enable in group' : 'Disable in group (keeps node, stops routing to it)'}>
                          {n.IsDisabled ? <PlayCircle className="h-3 w-3 text-green-500" /> : <PauseCircle className="h-3 w-3 text-amber-500" />}
                        </button>
                        {/* Remove from group */}
                        <button
                          onClick={async () => { await api.delete(`/platform/resources/recognizer-groups/${g.GroupId}/nodes/${n.NodeId}`); loadAll(); }}
                          className="hover:text-red-500" title="Remove from group">×</button>
                      </span>
                    ))}
                    {nodesForGroup(g.GroupId).length === 0 && <span className="text-xs text-gray-400 italic">No nodes assigned</span>}
                    {/* Assign node inline */}
                    {nodes.filter(n => !rGroupMembers.some(m => m.RecognizerGroupId === g.GroupId && m.NodeId === n.NodeId)).length > 0 && (
                      <div className="flex items-center gap-1">
                        <select
                          value={assigningNode[g.GroupId] || ''}
                          onChange={e => setAssigningNode(prev => ({ ...prev, [g.GroupId]: e.target.value }))}
                          className="text-xs border rounded-full px-2 py-0.5 text-gray-600 hover:border-indigo-400 cursor-pointer">
                          <option value="">+ Assign node…</option>
                          {nodes
                            .filter(n => !rGroupMembers.some(m => m.RecognizerGroupId === g.GroupId && m.NodeId === n.NodeId))
                            .map(n => <option key={n.NodeId} value={n.NodeId}>{n.NodeName}</option>)}
                        </select>
                        {assigningNode[g.GroupId] && (
                          <button
                            onClick={() => handleAssignNodeToGroup(g.GroupId)}
                            className="px-2 py-0.5 text-xs bg-indigo-600 text-white rounded-full hover:bg-indigo-700">
                            Add
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Analytics bar for group */}
                  {showAnalytics && analyticsForGroup(g.GroupId).length > 0 && (() => {
                    const rows = analyticsForGroup(g.GroupId);
                    const total = rows.reduce((s, r) => s + (r.Jobs7d || 0), 0);
                    return (
                      <div className="space-y-1">
                        <div className="text-xs text-gray-400 font-medium">Distribution (7d)</div>
                        {rows.map(r => {
                          const pct = total > 0 ? Math.round((r.Jobs7d / total) * 100) : 0;
                          return (
                            <div key={r.NodeId} className="flex items-center gap-2 text-xs">
                              <span className="w-24 truncate text-gray-600">{r.NodeName}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-1.5">
                                <div className="bg-indigo-400 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="w-8 text-right text-gray-400">{pct}%</span>
                              <span className="w-12 text-right text-gray-300">{r.Jobs7d}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Assignment rules */}
                  {rAssignments.filter(a => a.RecognizerGroupId === g.GroupId).map(a => (
                    <div key={a.AssignmentId} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1.5">
                      <Zap className="h-3 w-3 text-amber-500 flex-shrink-0" />
                      <span className="capitalize font-medium">{a.UploadType}</span> uploads
                      <span className="text-gray-400">→</span>
                      <span className="capitalize">{a.Scope}</span>
                      {a.TargetId && <span className="text-indigo-500 font-mono">{a.TargetId.slice(0,8)}…</span>}
                      <span className="ml-auto text-gray-400">priority {a.Priority}</span>
                      <button
                        onClick={() => setEditingRule({ AssignmentId: a.AssignmentId, uploadType: a.UploadType, scope: a.Scope, targetId: a.TargetId || '', priority: String(a.Priority) })}
                        className="text-gray-400 hover:text-indigo-600" title="Edit rule">
                        <Edit2 className="h-3 w-3" />
                      </button>
                      <button onClick={async () => { await api.delete(`/platform/resources/recognizer-assignments/${a.AssignmentId}`); loadAll(); }}
                        className="text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                  <AddAssignmentForm groupId={g.GroupId} cGroups={cGroups} onSaved={loadAll} />
                </div>
              ))}
            </div>
          </div>

          {/* Add Node modal */}
          {showAddNode && (
            <Modal title="Add Recognizer Node" onClose={() => setShowAddNode(false)}>
              <FormField label="Node Name" value={newNode.nodeName} onChange={v => setNewNode(p => ({...p, nodeName: v}))} />
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Provider Type</span>
                <select value={newNode.nodeType} onChange={e => setNewNode(p => ({...p, nodeType: e.target.value}))}
                  className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="azure_di">Azure Document Intelligence</option>
                  <option value="custom_http">Custom HTTP OCR</option>
                </select>
              </label>
              <FormField label="Endpoint URL" value={newNode.endpoint} onChange={v => setNewNode(p => ({...p, endpoint: v}))}
                placeholder={newNode.nodeType === 'azure_di' ? 'https://xxxx.cognitiveservices.azure.com/' : 'https://your-ocr-service.com/api'} />
              <FormField label="API Key / Bearer Token" value={newNode.apiKey} onChange={v => setNewNode(p => ({...p, apiKey: v}))} secret />
              {newNode.nodeType === 'custom_http' && (
                <label className="block space-y-1">
                  <span className="text-sm text-gray-700">Model Config <span className="text-gray-400 font-normal">(optional JSON)</span></span>
                  <textarea value={newNode.modelConfig} onChange={e => setNewNode(p => ({...p, modelConfig: e.target.value}))}
                    placeholder='{"modelId":"invoice-v1","fieldMap":{"InvoiceTotal":"total_amount"}}'
                    rows={3} className="block w-full border rounded-lg px-3 py-2 text-xs font-mono" />
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={newNode.isPreview} onChange={e => setNewNode(p => ({...p, isPreview: e.target.checked}))} className="rounded" />
                Mark as Preview <span className="text-gray-400 font-normal text-xs">(only routable via explicit rules)</span>
              </label>
              <button onClick={handleAddNode} disabled={saving} className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Add Node'}
              </button>
            </Modal>
          )}

          {/* Add Recognizer Group modal */}
          {showAddRGroup && (
            <Modal title="Add Recognizer Group" onClose={() => setShowAddRGroup(false)}>
              <FormField label="Group Name" value={newRGroup.groupName} onChange={v => setNewRGroup(p => ({...p, groupName: v}))} />
              <FormField label="Description (optional)" value={newRGroup.description} onChange={v => setNewRGroup(p => ({...p, description: v}))} />
              <button onClick={handleAddRGroup} disabled={saving} className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Create Group'}
              </button>
            </Modal>
          )}

          {/* Edit Recognizer Group modal */}
          {editingGroup && (
            <Modal title="Edit Recognizer Group" onClose={() => setEditingGroup(null)}>
              <FormField label="Group Name" value={editingGroup.groupName} onChange={v => setEditingGroup(p => ({...p, groupName: v}))} />
              <FormField label="Description (optional)" value={editingGroup.description} onChange={v => setEditingGroup(p => ({...p, description: v}))} />
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Load Strategy</span>
                <select value={editingGroup.loadStrategy} onChange={e => setEditingGroup(p => ({...p, loadStrategy: e.target.value}))}
                  className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="round_robin">Round-robin — cycle through nodes in order</option>
                  <option value="least_busy">Least busy — route to node with fewest active jobs</option>
                  <option value="random">Random — pick a random available node</option>
                </select>
              </label>
              <div className="flex gap-2">
                <button onClick={handleSaveEditGroup} disabled={saving} className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditingGroup(null)} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
              </div>
            </Modal>
          )}

          {/* Edit Assignment Rule modal */}
          {editingRule && (
            <Modal title="Edit Assignment Rule" onClose={() => setEditingRule(null)}>
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Upload Type</span>
                <select value={editingRule.uploadType} onChange={e => setEditingRule(p => ({...p, uploadType: e.target.value}))}
                  className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="any">Any</option>
                  <option value="single">Single only</option>
                  <option value="bulk">Bulk only</option>
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Scope</span>
                <select value={editingRule.scope} onChange={e => setEditingRule(p => ({...p, scope: e.target.value, targetId: ''}))}
                  className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="all">All companies</option>
                  <option value="company">Specific company</option>
                  <option value="company_group">Company group</option>
                </select>
              </label>
              {editingRule.scope === 'company' && (
                <label className="block space-y-1">
                  <span className="text-sm text-gray-700">Company</span>
                  <select value={editingRule.targetId} onChange={e => setEditingRule(p => ({...p, targetId: e.target.value}))}
                    className="block w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">— select company —</option>
                    {allCompanies.map(c => <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>)}
                  </select>
                </label>
              )}
              {editingRule.scope === 'company_group' && (
                <label className="block space-y-1">
                  <span className="text-sm text-gray-700">Company Group</span>
                  <select value={editingRule.targetId} onChange={e => setEditingRule(p => ({...p, targetId: e.target.value}))}
                    className="block w-full border rounded-lg px-3 py-2 text-sm">
                    <option value="">— select group —</option>
                    {cGroups.map(g => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
                  </select>
                </label>
              )}
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Priority (lower = checked first)</span>
                <input type="number" value={editingRule.priority} onChange={e => setEditingRule(p => ({...p, priority: e.target.value}))}
                  className="block w-full border rounded-lg px-3 py-2 text-sm" />
              </label>
              <div className="flex gap-2">
                <button onClick={handleSaveEditRule} disabled={saving || (editingRule.scope !== 'all' && !editingRule.targetId)}
                  className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditingRule(null)} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200">Cancel</button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* ── Tab 1: Company Groups ─────────────────────────────────────────── */}
      {tab === 1 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Users className="h-4 w-4" /> Company Groups</h2>
            <button onClick={() => setShowAddCGroup(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50">
              <Plus className="h-4 w-4" /> Add Group
            </button>
          </div>
          <div className="space-y-3">
            {cGroups.map(g => (
              <CompanyGroupCard key={g.GroupId} group={g}
                members={membersForCGroup(g.GroupId)}
                onRemove={async (companyId) => { await api.delete(`/platform/resources/company-groups/${g.GroupId}/members/${companyId}`); loadAll(); }}
                onAdd={async (companyId) => { await api.post(`/platform/resources/company-groups/${g.GroupId}/members`, { companyId }); loadAll(); }}
                onDelete={async () => {
                  if (!confirm(`Delete group "${g.GroupName}"?`)) return;
                  await api.delete(`/platform/resources/company-groups/${g.GroupId}`);
                  loadAll();
                }}
                onEdit={async (name, desc) => {
                  await api.patch(`/platform/resources/company-groups/${g.GroupId}`, { groupName: name, description: desc });
                  loadAll();
                }}
              />
            ))}
            {cGroups.length === 0 && <p className="text-sm text-gray-400 italic">No company groups yet.</p>}
          </div>

          {/* Unassigned companies panel */}
          {(() => {
            const unassigned = allCompanies.filter(c => !assignedCompanyIds.has(c.CompanyID));
            if (unassigned.length === 0) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {unassigned.length} {unassigned.length === 1 ? 'company is' : 'companies are'} not in any group — using platform default recognizer
                </div>
                <div className="flex flex-wrap gap-2">
                  {unassigned.map(c => (
                    <span key={c.CompanyID} className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-300">
                      {c.CompanyName}
                    </span>
                  ))}
                </div>
              </div>
            );
          })()}

          {showAddCGroup && (
            <Modal title="Add Company Group" onClose={() => setShowAddCGroup(false)}>
              <FormField label="Group Name" value={newCGroup.groupName} onChange={v => setNewCGroup(p => ({...p, groupName: v}))} />
              <FormField label="Description (optional)" value={newCGroup.description} onChange={v => setNewCGroup(p => ({...p, description: v}))} />
              <button onClick={handleAddCGroup} disabled={saving} className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Create Group'}
              </button>
            </Modal>
          )}
        </div>
      )}

      {/* ── Tab 2: Storage Providers ──────────────────────────────────────── */}
      {tab === 2 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800 flex items-center gap-2"><Database className="h-4 w-4" /> Storage Providers</h2>
            <button onClick={() => setShowAddStorage(true)} className="flex items-center gap-1 px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50">
              <Plus className="h-4 w-4" /> Add Provider
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {storageProviders.map(p => (
              <div key={p.ProviderId} className="bg-white border rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-gray-800 flex items-center gap-2">
                    <Database className="h-4 w-4 text-indigo-500" />{p.ProviderName}
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${p.IsActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                    {p.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="text-xs text-gray-400">Added {new Date(p.AddedAt).toLocaleDateString()}</div>
                {storageAssignments.filter(a => a.ProviderId === p.ProviderId).map(a => (
                  <div key={a.AssignmentId} className="flex items-center gap-2 text-xs text-gray-600 bg-gray-50 rounded px-2 py-1">
                    <span className="capitalize">{a.Scope}</span>
                    {a.Scope === 'company' && (() => { const c = allCompanies.find(x => x.CompanyID === a.TargetId); return c ? <span className="text-indigo-600 font-medium">{c.CompanyName}</span> : a.TargetId && <span className="text-indigo-500 font-mono">{a.TargetId.slice(0,8)}…</span>; })()}
                    {a.Scope === 'company_group' && (() => { const g = cGroups.find(x => x.GroupId === a.TargetId); return g ? <span className="text-indigo-600 font-medium">{g.GroupName}</span> : a.TargetId && <span className="text-indigo-500 font-mono">{a.TargetId.slice(0,8)}…</span>; })()}
                    <span className="ml-auto text-gray-400">priority {a.Priority}</span>
                    <button onClick={async () => { await api.delete(`/platform/resources/storage-assignments/${a.AssignmentId}`); loadAll(); }}
                      className="text-red-400 hover:text-red-600"><Trash2 className="h-3 w-3" /></button>
                  </div>
                ))}
                <AddStorageAssignmentForm providerId={p.ProviderId} allCompanies={allCompanies} cGroups={cGroups} onSaved={loadAll} />
                <button
                  onClick={() => { setShowMigrateModal(p.ProviderId); setMigrateCompany(''); setMigrateToProvider(''); }}
                  className="text-xs text-gray-400 hover:text-indigo-600 flex items-center gap-1 pt-1">
                  <RefreshCw className="h-3 w-3" /> Migrate company here
                </button>
              </div>
            ))}
            {storageProviders.length === 0 && <p className="text-sm text-gray-400 italic col-span-2">No custom storage providers.</p>}
          </div>
          {showAddStorage && (
            <Modal title="Add Storage Provider" onClose={() => setShowAddStorage(false)}>
              <FormField label="Provider Name" value={newStorage.providerName} onChange={v => setNewStorage(p => ({...p, providerName: v}))} />
              <FormField label="Azure Storage Connection String" value={newStorage.connectionString} onChange={v => setNewStorage(p => ({...p, connectionString: v}))} secret />
              <button onClick={handleAddStorage} disabled={saving} className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {saving ? 'Saving…' : 'Add Provider'}
              </button>
            </Modal>
          )}
          {migrations.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-gray-700 text-sm flex items-center gap-2">
                <RefreshCw className="h-4 w-4" /> Storage Migrations
              </h3>
              {migrations.map(m => (
                <MigrationRow key={m.MigrationId} migration={m}
                  providers={storageProviders} companies={allCompanies}
                  onCancel={async () => { await api.post(`/platform/resources/storage-migrations/${m.MigrationId}/cancel`); loadMigrations(); }}
                />
              ))}
            </div>
          )}
          {showMigrateModal && (
            <Modal title="Migrate Company to Storage Provider" onClose={() => setShowMigrateModal(null)}>
              <p className="text-xs text-gray-500">Copies all company blobs to the target provider and updates routing. Source blobs are <strong>not</strong> deleted.</p>
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Company</span>
                <select value={migrateCompany} onChange={e => setMigrateCompany(e.target.value)} className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="">— select company —</option>
                  {allCompanies.map(c => <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>)}
                </select>
              </label>
              <label className="block space-y-1">
                <span className="text-sm text-gray-700">Destination Provider</span>
                <select value={migrateToProvider} onChange={e => setMigrateToProvider(e.target.value)} className="block w-full border rounded-lg px-3 py-2 text-sm">
                  <option value="">— select destination —</option>
                  {storageProviders.filter(p => p.ProviderId !== showMigrateModal).map(p => (
                    <option key={p.ProviderId} value={p.ProviderId}>{p.ProviderName}</option>
                  ))}
                </select>
              </label>
              <button onClick={handleStartMigration} disabled={migratingNow || !migrateCompany || !migrateToProvider}
                className="w-full py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {migratingNow ? 'Starting…' : 'Start Migration'}
              </button>
            </Modal>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function NodeCard({ node, liveActiveJobs, nodeAnalytics, showAnalytics, onToggle, onDelete }) {
  const lastUsed = node.LastUsedAt ? new Date(node.LastUsedAt).toLocaleString() : 'Never';
  return (
    <div className={`bg-white border rounded-xl p-4 space-y-3 ${!node.IsActive ? 'opacity-60' : ''}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="font-semibold text-gray-800 flex items-center gap-2 flex-wrap">
            <Server className="h-4 w-4 text-indigo-500" />
            {node.NodeName}
            {node.ApiKeyEncrypted === '****' && node.Endpoint === 'ENV_VAR_FALLBACK' && (
              <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">env-var</span>
            )}
            {node.NodeType === 'custom_http' && (
              <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">Custom HTTP</span>
            )}
            {node.IsPreview && (
              <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">PREVIEW</span>
            )}
          </div>
          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{node.Endpoint !== 'ENV_VAR_FALLBACK' ? node.Endpoint : '(from environment)'}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={onToggle} title={node.IsActive ? 'Deactivate' : 'Activate'}
            className={`p-1.5 rounded-lg ${node.IsActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}>
            {node.IsActive ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
          </button>
          <button onClick={onDelete} className="p-1.5 rounded-lg text-red-400 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="bg-indigo-50 rounded p-2"><div className="font-bold text-indigo-700">{liveActiveJobs ?? 0}</div><div className="text-gray-500">Active</div></div>
        <div className="bg-gray-50 rounded p-2"><div className="font-bold text-gray-700">{node.TotalProcessed ?? 0}</div><div className="text-gray-500">Total</div></div>
        <div className="bg-gray-50 rounded p-2"><div className="font-bold text-gray-700 text-xs">{node.GroupNames || '—'}</div><div className="text-gray-500">Groups</div></div>
      </div>
      {showAnalytics && nodeAnalytics && (
        <div className="grid grid-cols-3 gap-2 text-center text-xs border-t pt-2">
          <div><div className="font-bold text-gray-700">{nodeAnalytics.Jobs7d ?? 0}</div><div className="text-gray-400">7d jobs</div></div>
          <div><div className="font-bold text-gray-700">{nodeAnalytics.AvgWaitSec != null ? `${Math.round(nodeAnalytics.AvgWaitSec)}s` : '—'}</div><div className="text-gray-400">avg wait</div></div>
          <div className={nodeAnalytics.Failed7d > 0 ? 'text-red-600' : ''}>
            <div className="font-bold">{nodeAnalytics.Failed7d ?? 0}</div><div className="text-gray-400">failed</div>
          </div>
        </div>
      )}
      {node.LastError && (
        <div className="text-xs text-red-600 bg-red-50 rounded p-2 flex gap-1 items-start">
          <AlertTriangle className="h-3 w-3 flex-shrink-0 mt-0.5" />
          <span className="truncate">{node.LastError}</span>
        </div>
      )}
      <div className="text-xs text-gray-400">Last used: {lastUsed}</div>
    </div>
  );
}

function CompanyGroupCard({ group, members, onRemove, onAdd, onDelete, onEdit }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(group.GroupName);
  const [editDesc, setEditDesc] = useState(group.Description || '');
  const [companies, setCompanies] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const memberIds = new Set(members.map(m => m.CompanyId));

  const openAdd = () => {
    setShowAdd(true);
    setSelectedId('');
    setErr(null);
    api.get('/companies').then(r => setCompanies(Array.isArray(r.data) ? r.data : (r.data.companies || []))).catch(() => {});
  };

  const handleAdd = async () => {
    if (!selectedId) return;
    setAdding(true); setErr(null);
    try { await onAdd(selectedId); setShowAdd(false); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to add'); }
    finally { setAdding(false); }
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try { await onEdit(editName, editDesc); setEditing(false); }
    catch (e) { setErr(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        {editing ? (
          <div className="flex-1 flex gap-2 items-center">
            <input value={editName} onChange={e => setEditName(e.target.value)}
              className="border rounded px-2 py-1 text-sm flex-1" placeholder="Group name" />
            <input value={editDesc} onChange={e => setEditDesc(e.target.value)}
              className="border rounded px-2 py-1 text-xs w-40" placeholder="Description" />
            <button onClick={handleSaveEdit} disabled={saving || !editName}
              className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-40">
              {saving ? '…' : 'Save'}
            </button>
            <button onClick={() => setEditing(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
        ) : (
          <div className="font-medium text-gray-800 flex items-center gap-2">
            {group.GroupName}
            {group.Description && <span className="text-xs text-gray-400">{group.Description}</span>}
          </div>
        )}
        {!editing && (
          <div className="flex gap-1">
            <button onClick={() => { setEditing(true); setEditName(group.GroupName); setEditDesc(group.Description || ''); }}
              className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Edit">
              <Edit2 className="h-3.5 w-3.5" />
            </button>
            <button onClick={openAdd} className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 border border-indigo-200">
              <Plus className="h-3 w-3" /> Add company
            </button>
            <button onClick={onDelete}
              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete group">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {members.map(m => (
          <span key={m.CompanyId} className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1">
            {m.CompanyName}
            <button onClick={() => onRemove(m.CompanyId)} title="Remove from group"
              className="ml-1 text-blue-400 hover:text-red-500"><XCircle className="h-3 w-3" /></button>
          </span>
        ))}
        {members.length === 0 && <span className="text-xs text-gray-400 italic">No companies assigned</span>}
      </div>
      {showAdd && (
        <div className="bg-indigo-50 rounded p-3 space-y-2">
          <select value={selectedId} onChange={e => setSelectedId(e.target.value)}
            className="block w-full border rounded px-2 py-1.5 text-sm">
            <option value="">— select company to add —</option>
            {companies.filter(c => !memberIds.has(c.CompanyID)).map(c => (
              <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>
            ))}
          </select>
          {err && <div className="text-xs text-red-600">{err}</div>}
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={adding || !selectedId}
              className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-40">
              {adding ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => setShowAdd(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function AddAssignmentForm({ groupId, cGroups, onSaved }) {
  const [show, setShow] = useState(false);
  const [uploadType, setUploadType] = useState('any');
  const [scope, setScope] = useState('all');
  const [targetId, setTargetId] = useState('');
  const [priority, setPriority] = useState('10');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    if (!show) return;
    api.get('/companies').then(r => setCompanies(Array.isArray(r.data) ? r.data : (r.data.companies || []))).catch(() => {});
  }, [show]);

  const handleScopeChange = (val) => { setScope(val); setTargetId(''); };

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      await api.post('/platform/resources/recognizer-assignments', {
        recognizerGroupId: groupId, uploadType, scope,
        targetId: scope !== 'all' ? targetId : null,
        priority: parseInt(priority, 10),
      });
      setShow(false);
      onSaved();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!show) return (
    <button onClick={() => setShow(true)} className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
      <Plus className="h-3 w-3" /> Add assignment rule
    </button>
  );

  return (
    <div className="bg-indigo-50 rounded p-3 space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-gray-600">Upload type</span>
          <select value={uploadType} onChange={e => setUploadType(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="any">Any</option>
            <option value="single">Single only</option>
            <option value="bulk">Bulk only</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Scope</span>
          <select value={scope} onChange={e => handleScopeChange(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="all">All companies</option>
            <option value="company">Specific company</option>
            <option value="company_group">Company group</option>
          </select>
        </label>
      </div>
      {scope === 'company' && (
        <label className="block">
          <span className="text-xs text-gray-600">Company</span>
          <select value={targetId} onChange={e => setTargetId(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="">— select company —</option>
            {companies.map(c => <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>)}
          </select>
        </label>
      )}
      {scope === 'company_group' && (
        <label className="block">
          <span className="text-xs text-gray-600">Company Group</span>
          <select value={targetId} onChange={e => setTargetId(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="">— select group —</option>
            {(cGroups || []).map(g => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
          </select>
        </label>
      )}
      {saveError && <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{saveError}</div>}
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-600">Priority</label>
        <input type="number" value={priority} onChange={e => setPriority(e.target.value)} className="w-16 border rounded px-2 py-1 text-xs" />
        <button onClick={handleSave} disabled={saving || (scope !== 'all' && !targetId)} className="ml-auto px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-40">{saving ? 'Saving…' : 'Save'}</button>
        <button onClick={() => setShow(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    </div>
  );
}

function AddStorageAssignmentForm({ providerId, allCompanies, cGroups, onSaved }) {
  const [show, setShow] = useState(false);
  const [scope, setScope] = useState('all');
  const [targetId, setTargetId] = useState('');
  const [priority, setPriority] = useState('10');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const handleScopeChange = (val) => { setScope(val); setTargetId(''); };

  const handleSave = async () => {
    setSaving(true); setSaveError(null);
    try {
      await api.post('/platform/resources/storage-assignments', {
        providerId, scope,
        targetId: scope !== 'all' ? targetId : null,
        priority: parseInt(priority, 10),
      });
      setShow(false);
      onSaved();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (!show) return (
    <button onClick={() => setShow(true)} className="text-xs text-indigo-500 hover:text-indigo-700 flex items-center gap-1">
      <Plus className="h-3 w-3" /> Add assignment rule
    </button>
  );

  return (
    <div className="bg-indigo-50 rounded p-3 space-y-2 text-sm">
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-xs text-gray-600">Scope</span>
          <select value={scope} onChange={e => handleScopeChange(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="all">All companies</option>
            <option value="company">Specific company</option>
            <option value="company_group">Company group</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-gray-600">Priority</span>
          <input type="number" value={priority} onChange={e => setPriority(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs" />
        </label>
      </div>
      {scope === 'company' && (
        <label className="block">
          <span className="text-xs text-gray-600">Company</span>
          <select value={targetId} onChange={e => setTargetId(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="">— select company —</option>
            {allCompanies.map(c => <option key={c.CompanyID} value={c.CompanyID}>{c.CompanyName}</option>)}
          </select>
        </label>
      )}
      {scope === 'company_group' && (
        <label className="block">
          <span className="text-xs text-gray-600">Company Group</span>
          <select value={targetId} onChange={e => setTargetId(e.target.value)} className="block w-full mt-1 border rounded px-2 py-1 text-xs">
            <option value="">— select group —</option>
            {(cGroups || []).map(g => <option key={g.GroupId} value={g.GroupId}>{g.GroupName}</option>)}
          </select>
        </label>
      )}
      {saveError && <div className="text-xs text-red-600 bg-red-50 rounded px-2 py-1">{saveError}</div>}
      <div className="flex gap-2 justify-end">
        <button onClick={handleSave} disabled={saving || (scope !== 'all' && !targetId)}
          className="px-3 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-40">
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button onClick={() => setShow(false)} className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    </div>
  );
}

function MigrationRow({ migration: m, providers, companies, onCancel }) {
  const toProvider   = providers.find(p => p.ProviderId === m.ToProviderId);
  const fromProvider = providers.find(p => p.ProviderId === m.FromProviderId);
  const company      = companies.find(c => c.CompanyID  === m.CompanyId);
  const pct = m.TotalBlobs ? Math.round((m.MovedBlobs / m.TotalBlobs) * 100) : null;
  const statusColor = {
    pending:   'bg-gray-100 text-gray-600',
    running:   'bg-blue-100 text-blue-700',
    done:      'bg-green-100 text-green-700',
    failed:    'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-400',
  }[m.Status] || 'bg-gray-100 text-gray-600';

  return (
    <div className="bg-white border rounded-lg p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm flex-wrap">
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor}`}>{m.Status}</span>
        <span className="font-medium text-gray-800">{company?.CompanyName || m.CompanyId.slice(0,8)+'…'}</span>
        <span className="text-gray-400 text-xs">{fromProvider?.ProviderName || 'env-var'} → {toProvider?.ProviderName || 'unknown'}</span>
        {(m.Status === 'running' || m.Status === 'pending') && (
          <button onClick={onCancel} className="ml-auto text-xs text-red-400 hover:text-red-600">Cancel</button>
        )}
        {m.Status !== 'running' && m.Status !== 'pending' && (
          <span className="ml-auto text-xs text-gray-400">
            {m.CompletedAt ? new Date(m.CompletedAt).toLocaleString() : new Date(m.CreatedAt).toLocaleString()}
          </span>
        )}
      </div>
      {pct !== null && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-gray-500">
            <span>{m.MovedBlobs} / {m.TotalBlobs} blobs</span>
            {m.FailedBlobs > 0 && <span className="text-red-500">{m.FailedBlobs} failed</span>}
            <span>{pct}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-1.5">
            <div className={`h-1.5 rounded-full transition-all ${m.Status === 'done' ? 'bg-green-500' : m.Status === 'failed' ? 'bg-red-400' : 'bg-blue-500'}`}
              style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XCircle className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FormField({ label, value, onChange, placeholder, secret }) {
  const [show, setShow] = useState(!secret);
  return (
    <label className="block space-y-1">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder || ''}
          className="block w-full border rounded-lg px-3 py-2 text-sm pr-10"
        />
        {secret && (
          <button type="button" onClick={() => setShow(s => !s)} className="absolute right-2 top-2 text-gray-400">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
    </label>
  );
}

